import { configClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { ContentAttributeApiCategory, KpiApiItem } from '../types/config.types';
import type { ContentAttribute, KpiOption } from '../types/create-experiment.types';

/**
 * Fetch the KPI catalogue for a platform. Maps the API shape to the UI's
 * existing `KpiOption` type so dropdown components don't need to change.
 * Sort order is honoured on both the response and the rendered list.
 */
export async function fetchKpis(platform: string): Promise<KpiOption[]> {
  const data = await configClient.get(endpoints.config.kpis(platform)).json<KpiApiItem[]>();
  return [...data]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((item) => ({
      value: item.id,
      label: item.name,
      category: item.category,
      subLabel: item.source,
    }));
}

/**
 * Fetch the content-attribute taxonomy. The hierarchy maps onto the UI's
 * `ContentAttribute` type — categories become parents, the inner `attributes`
 * array becomes the toggleable sub-pills. `enabled` defaults to `true` since
 * the API has no per-row disabled bit today.
 */
export async function fetchContentAttributes(): Promise<ContentAttribute[]> {
  const data = await configClient
    .get(endpoints.config.contentAttributes())
    .json<ContentAttributeApiCategory[]>();
  return [...data]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((cat) => ({
      id: cat.id,
      label: cat.name,
      description: cat.description,
      enabled: true,
      subAttributes: [...cat.attributes]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((sub) => ({ id: sub.id, key: sub.key, label: sub.name })),
    }));
}
